// ==============================
// ULTRA-FAST Vendor Images for Facet Labels
// Optimized for instant loading with preloading
// ==============================
function addVendorImages() {
    let mutationObserver = null;
    let imageCache = new Map();
    let preloadedImages = new Set();
    let debounceTimer = null;

    // Preload common brand images immediately
    const preloadBrandImages = () => {
        const commonBrands = [
            'optimum-nutrition', 'dymatize', 'bsn', 'cellucor', 'ghost',
            'muscletech', 'quest-nutrition', 'rule1', 'nutrex', 'gaspari'
        ];

        commonBrands.forEach(brand => {
            if (!preloadedImages.has(brand)) {
                const img = new Image();
                img.src = `https://www.elitesupps.com.au/cdn/shop/files/${brand}.png`;
                img.onload = () => imageCache.set(brand, img.src);
                preloadedImages.add(brand);
            }
        });
    };

    // Wait for facets to be available - OPTIMIZED
    const waitForFacets = async (maxAttempts = 20, delay = 50) => {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            const atomicFacets = document.querySelectorAll('atomic-commerce-facet[field="ec_brand"]');

            if (atomicFacets.length > 0) {
                const readyFacets = Array.from(atomicFacets).filter(f => f.facet && f.facet.state);
                if (readyFacets.length > 0) {
                    return atomicFacets;
                }
            }
            await new Promise(resolve => setTimeout(resolve, delay));
        }
        return null;
    };

    // Debounced apply function to reduce excessive calls
    const debouncedApply = (vendorFacets, forceRefresh = false) => {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => applyVendorImages(vendorFacets, forceRefresh), 16); // ~60fps
    };

    // ULTRA-FAST vendor image application with batching
    const applyVendorImages = (vendorFacets, forceRefresh = false) => {
        const processBatch = (labels, startIndex = 0) => {
            const batchSize = 5; // Process 5 images at a time
            const endIndex = Math.min(startIndex + batchSize, labels.length);

            for (let i = startIndex; i < endIndex; i++) {
                const { label, vendorName, isVisible } = labels[i];

                const existingImage = label.querySelector('.vendor-image');
                if (existingImage && forceRefresh) {
                    existingImage.remove();
                }

                if (isVisible && (!existingImage || forceRefresh)) {
                    let brand = vendorName
                        .replace(/[&\/\\#,+()$~%.'":*?<>{}]/g, '')
                        .trim()
                        .replace(/\s+/g, '-')
                        .toLowerCase();

                    // Use cached image if available
                    let brandImage;
                    if (imageCache.has(brand)) {
                        brandImage = imageCache.get(brand);
                    } else {
                        brandImage = `https://www.elitesupps.com.au/cdn/shop/files/${brand}.png`;
                        // Preload this image for next time
                        const preImg = new Image();
                        preImg.src = brandImage;
                        preImg.onload = () => imageCache.set(brand, brandImage);
                    }

                    // Use actual img element for better browser optimization
                    const img = document.createElement('img');
                    img.className = 'vendor-image';
                    img.src = brandImage;
                    img.alt = vendorName;
                    img.loading = 'eager'; // Prioritize loading

                    // Apply styles immediately
                    Object.assign(img.style, {
                        width: '40px',
                        height: '40px',
                        marginRight: '10px',
                        objectFit: 'cover',
                        display: 'inline-block',
                        verticalAlign: 'middle',
                        flexShrink: '0',
                        borderRadius: '50%',
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 0 8px -5px #212427'
                    });

                    // Apply label styles
                    Object.assign(label.style, {
                        display: 'flex',
                        alignItems: 'center'
                    });

                    label.insertBefore(img, label.firstChild);
                } else if (!isVisible && existingImage) {
                    existingImage.remove();
                }
            }

            // Process next batch if more labels remain
            if (endIndex < labels.length) {
                requestAnimationFrame(() => processBatch(labels, endIndex));
            }
        };

        // Collect all labels that need processing
        const allLabels = [];
        vendorFacets.forEach((vendorFacet) => {
            if (vendorFacet.shadowRoot) {
                const valueLabels = vendorFacet.shadowRoot.querySelectorAll('span[part="value-label"]');
                valueLabels.forEach(label => {
                    const vendorName = label.getAttribute('title') || label.textContent.trim();
                    const isVisible = label.offsetHeight > 0 && label.offsetWidth > 0;
                    allLabels.push({ label, vendorName, isVisible });
                });
            }
        });

        // Start batch processing
        if (allLabels.length > 0) {
            processBatch(allLabels);
        }
    };

    // Setup mutation observer for dynamic updates
    const setupMutationObserver = (vendorFacets) => {
        if (mutationObserver) mutationObserver.disconnect();

        mutationObserver = new MutationObserver((mutations) => {
            let shouldReapply = false;

            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    // Check for added nodes (new search results)
                    if (mutation.addedNodes.length > 0) {
                        const hasRelevantChanges = Array.from(mutation.addedNodes).some(node =>
                                node.nodeType === Node.ELEMENT_NODE && (
                                    node.matches?.('span[part="value-label"]') ||
                                    node.querySelector?.('span[part="value-label"]') ||
                                    node.matches?.('[part*="value"]') ||
                                    node.querySelector?.('[part*="value"]')
                                )
                        );

                        if (hasRelevantChanges) shouldReapply = true;
                    }

                    // Check for removed nodes (search filtering)
                    if (mutation.removedNodes.length > 0) {
                        shouldReapply = true;
                    }
                }

                // Enhanced attribute change detection for search filtering
                if (mutation.type === 'attributes' &&
                    ['style', 'hidden', 'class', 'aria-hidden'].includes(mutation.attributeName)) {
                    const target = mutation.target;
                    if (target.matches?.('span[part="value-label"]') ||
                        target.closest?.('span[part="value-label"]') ||
                        target.querySelector?.('span[part="value-label"]') ||
                        target.matches?.('[part*="value"]') ||
                        target.closest?.('[part*="value"]')) {
                        shouldReapply = true;
                    }
                }
            });

            if (shouldReapply) {
                // Immediate application for search results
                setTimeout(() => debouncedApply(vendorFacets, true), 10);
            }
        });

        vendorFacets.forEach(facet => {
            if (facet.shadowRoot) {
                // Enhanced mutation observer
                mutationObserver.observe(facet.shadowRoot, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    attributeFilter: ['style', 'hidden', 'class']
                });

                // Add input event listener for facet search - IMMEDIATE RESPONSE FOR SEARCH
                const searchInput = facet.shadowRoot.querySelector('input[part="search-input"]');
                if (searchInput) {
                    let searchTimeout;
                    searchInput.addEventListener('input', () => {
                        // Clear previous timeout
                        if (searchTimeout) clearTimeout(searchTimeout);

                        // Apply images immediately for search suggestions
                        applyVendorImages(vendorFacets, true);

                        // Also apply after a short delay to catch any delayed DOM updates
                        searchTimeout = setTimeout(() => {
                            applyVendorImages(vendorFacets, true);
                        }, 100);
                    });

                    // Also listen for keyup and search events
                    searchInput.addEventListener('keyup', () => {
                        setTimeout(() => applyVendorImages(vendorFacets, true), 50);
                    });

                    searchInput.addEventListener('search', () => {
                        setTimeout(() => applyVendorImages(vendorFacets, true), 50);
                    });
                }
            }
        });
    };

    // Main initialization
    const initialize = async () => {
        const vendorFacets = await waitForFacets();
        if (!vendorFacets) return;

        // Initial immediate application
        applyVendorImages(vendorFacets, true);
        setupMutationObserver(vendorFacets);

        // Start preloading immediately
        preloadBrandImages();

        // OPTIMIZED event handlers with immediate response for search
        document.addEventListener('atomic:facet:update', () => {
            // Immediate response for facet updates (includes search)
            applyVendorImages(vendorFacets, true);
            setTimeout(() => applyVendorImages(vendorFacets, true), 100); // Catch delayed updates
        });

        document.addEventListener('atomic:search:state-change', () => {
            applyVendorImages(vendorFacets, true);
            setTimeout(() => applyVendorImages(vendorFacets, true), 50);
        });

        // Additional search-related event listeners
        document.addEventListener('atomic:facet:search', () => {
            applyVendorImages(vendorFacets, true);
        });

        document.addEventListener('atomic:facet:clear-search', () => {
            setTimeout(() => applyVendorImages(vendorFacets, true), 50);
        });

        // Reduced navigation response
        window.addEventListener('popstate', () => debouncedApply(vendorFacets, true));

        // Visibility response only when needed
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                debouncedApply(vendorFacets, true);
            }
        });

        // Remove aggressive polling - only refresh on user interaction or visibility change
    };

    initialize();
}

// IMMEDIATE EXECUTION - Run as soon as possible for fastest load
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', addVendorImages);
} else {
    // Execute immediately if DOM is already ready
    addVendorImages();
}

// Also try running on next tick for extra coverage
setTimeout(addVendorImages, 0);


// ==============================
// Switch Between Grid and List View
// ==============================
function switchView(view) {
    const gridView = document.querySelector('.grid-view');
    const listView = document.querySelector('.list-view');
    const gridButton = document.querySelector('.view-toggle button[data-view="grid"]');
    const listButton = document.querySelector('.view-toggle button[data-view="list"]');

    if (view === 'grid') {
        gridView.style.display = 'block';
        listView.style.display = 'none';
        gridButton.classList.add('active');
        listButton.classList.remove('active');
    } else if (view === 'list') {
        gridView.style.display = 'none';
        listView.style.display = 'block';
        gridButton.classList.remove('active');
        listButton.classList.add('active');
    }

    // Update URL parameter to persist view state (only for list view)
    const url = new URL(window.location);
    if (view === 'list') {
        url.searchParams.set('view', 'list');
    } else {
        url.searchParams.delete('view');
    }
    window.history.pushState({}, '', url);
}

// Initialize view on page load based on URL parameter
function initializeView() {
    const url = new URL(window.location);
    const viewParam = url.searchParams.get('view');

    // Default to grid if no parameter, otherwise use the parameter value
    const view = viewParam === 'list' ? 'list' : 'grid';
    switchView(view);
}

// Run initialization when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeView);
} else {
    initializeView();
}
